/**
 * scheduleEnums.ts — Frontend constants for the Moftaki Scheduling module.
 *
 * Mirrors backend/src/constants/scheduleEnums.ts.
 * Used by: AdminUsersPage (WorkGroup/Tier dropdowns),
 *          SkillMatrixToolbar (WorkGroup filter),
 *          EmployeeSchedule row grouping (Phase 3).
 *          Test Catalogue master-detail page (Category selects in the list
 *          column, detail pane, SkillsDrawer, and TaskTypeEditForm).
 */

/**
 * Canonical top-level category shared by Skill, TaskType, and TestCatalogItem
 * for all rock taxonomy rows (REQ-02, Phase 4 item 4.1).
 *
 * Use this constant everywhere instead of the literal 'Rock Testing' so that
 * a single rename here propagates to all reads and writes.
 * Mirrors: backend/src/constants/scheduleEnums.ts ROCK_TESTING_CATEGORY.
 */
export const ROCK_TESTING_CATEGORY = 'Rock Testing' as const;
export type RockTestingCategory = typeof ROCK_TESTING_CATEGORY;

/**
 * The canonical, ordered list of Category values used by the test catalog
 * (TestCatalogItem.Category), the skill catalog (Skill.Category), and the
 * task-type catalog (TaskType.Category).
 *
 * REQ-02 / Phase 4 item 4.4: "only the category is shared" — both the skill
 * catalog and the test catalog draw their Category picklist from this single
 * source. Updating this array propagates to all three admin pickers
 * simultaneously, with no duplication.
 *
 * IMPORTANT: do NOT couple this to skill names or test names. Only the
 * Category string is shared; name fields remain fully independent.
 */
export const SCHEDULE_CATEGORY_OPTIONS = [
  'General / Index Testing',
  'Compaction Testing',
  ROCK_TESTING_CATEGORY,
  'Consolidation Testing',
  'Triaxial Testing',
  'Direct and Direct Simple Shear Testing',
  'Hydraulic Conductivity',
  'Unsaturated Soils Testing',
  'Other Advanced Tests',
  'Critical State Line Testing',
] as const;

export type ScheduleCategory = (typeof SCHEDULE_CATEGORY_OPTIONS)[number];

/**
 * Valid values for AppUser.WorkGroup.
 *
 * These are the lab's real team groupings (matches the "Main Team" column in the
 * staff workbook imported by backend/src/scripts/import-lab-staff-2026.ts, which
 * writes WorkGroup ← mainTeam). Keep this list in sync with
 * backend/src/constants/scheduleEnums.ts — the backend validates AppUser.WorkGroup
 * against the same array in userController.updateUser, so any value missing here is
 * also rejected on save (BUG-2026-0123: Rock/Soil were missing, blocking assignment).
 */
export const WORK_GROUP_OPTIONS = ['Lab', 'Soil', 'Rock', 'Large Scale'] as const;
export type WorkGroup = (typeof WORK_GROUP_OPTIONS)[number];

/**
 * Valid values for AppUser.Tier.
 * Maps to Employee Schedule row-group headers.
 */
export const TIER_OPTIONS = ['Lead', 'Senior', 'Tech', 'Junior'] as const;
export type Tier = (typeof TIER_OPTIONS)[number];

// NOTE: a TIER_DISPLAY_LABELS map used to live here but was never imported
// (the Employee Schedule groups via tierGroupLabel in employeeScheduleShared.ts,
// which collapses Senior+Tech). Removed in the RBAC audit cleanup (2026-07-10).

/**
 * Derive a scheduling Tier from a free-text Job Class / GAL job title.
 *
 * Mirrors backend/src/constants/scheduleEnums.ts `deriveTierFromJobClass` — keep
 * the two in sync. Used by the admin User Management page to show the auto-derived
 * Tier as a hint when none is explicitly set. The backend remains the source of
 * truth that persists the derived Tier; this is display-only on the FE.
 */
export function deriveTierFromJobClass(jobClass: string | null | undefined): Tier | null {
  if (!jobClass) return null;
  const s = jobClass.toLowerCase();
  if (/\b(lead|principal|supervisor|manager|chief|head|director)\b/.test(s)) return 'Lead';
  if (/\b(senior|sr)\b/.test(s)) return 'Senior';
  if (/\b(junior|jr|student|intern|trainee|apprentice|co-?op|entry)\b/.test(s)) return 'Junior';
  if (/\b(intermediate|technician|technologist|tech|analyst|operator|specialist|engineer|geologist|scientist)\b/.test(s)) return 'Tech';
  return null;
}

// ---------------------------------------------------------------------------
// Skill levels (BUG-2026-0124 — expanded 1–4 → 1–5)
// ---------------------------------------------------------------------------
/**
 * Canonical skill-level scale for the Skill Matrix.
 *
 * BUG-2026-0124 expanded the scale from 1–4 to 1–5, splitting the former
 * top tier ("Mastered, can evaluate others") into a Reviewer tier (4) and a
 * Trainer tier (5). The full definitions below are the source of truth — the
 * chip labels, legend, popover, action-bar buttons and number-key hotkeys all
 * draw from here, and the backend mirrors MAX_SKILL_LEVEL in
 * backend/src/constants/scheduleEnums.ts for write-time validation.
 *
 * NOTE: the DB CHECK constraint CK_SM_Level must allow 1–5 (migration 077).
 */
export const MAX_SKILL_LEVEL = 5 as const;

/**
 * RBAC overhaul Phase C (2026-08-06, migration 125): out-of-band levels beyond
 * the 1–5 proficiency scale. Level 4+ reviews a test's data (admins bypass);
 * level 9 marks an "approver designate" — the ~4 higher-ups who approve tests
 * OUTSIDE the system and whose names populate the Approved By dropdown (only
 * admins write the field, on their behalf). 6–8 stay reserved for future
 * ladder growth. Mirrors backend/src/constants/scheduleEnums.ts.
 */
export const REVIEWER_MIN_SKILL_LEVEL = 4 as const;
export const APPROVER_SKILL_LEVEL = 9 as const;

/** Ordered list of valid skill levels (1 … MAX_SKILL_LEVEL, then 9). */
export const SKILL_LEVELS = [1, 2, 3, 4, 5, APPROVER_SKILL_LEVEL] as const;
export type SkillLevelValue = (typeof SKILL_LEVELS)[number];

/** Per-level metadata: short code, concise label, and the full BGC definition. */
export interface SkillLevelMeta {
  /** Short code, e.g. "L1". */
  short: string;
  /** Concise label for chips/tooltips. */
  label: string;
  /** Full BGC definition (BUG-2026-0124 spec). */
  description: string;
}

export const SKILL_LEVEL_META: Record<SkillLevelValue, SkillLevelMeta> = {
  1: {
    short: 'L1',
    label: 'Has not begun training',
    description: 'Has not begun training',
  },
  2: {
    short: 'L2',
    label: 'Training ongoing',
    description:
      'Training is ongoing but is not yet proficient to carry out test alone without periodic supervision',
  },
  3: {
    short: 'L3',
    label: 'Proficient (can train)',
    description:
      'Is proficient and can carry out test alone without periodic supervision, can provide training on the method, but cannot review',
  },
  4: {
    short: 'L4',
    label: 'Reviewer',
    description:
      '(Reviewer) Has demonstrated knowledge necessary to review data, but not to provide training on BGC methods',
  },
  5: {
    short: 'L5',
    label: 'Trainer',
    description:
      '(Trainer) Has mastered the test using the BGC method and can evaluate the skills of others, including Reviewer',
  },
  9: {
    short: 'L9',
    label: 'Approver',
    description:
      '(Approver designate) Approves test results outside the system; appears in the Approved By list for tests in this skill. Recorded on their behalf by a Lab Admin.',
  },
};
